import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { ScheduleInput } from '../features/offerModal/types';
import {
  buildScheduleOptimisticUpdateData,
  buildSchedulePatchData,
  buildSchedulePostData,
} from '../features/offerModal/utils';
import { mapFrequencyToApiFormat, mapFrequencyToCacheFormat } from './frequencyUtils';
import { determineOfferStatus, determineOptimisticIsEnabled } from './offerStatusUtils';
import { invalidateContentArtifacts } from './useArtifacts';

type Referrer = paths['/offers/benchmark']['post']['requestBody']['content']['application/json']['referrer'];

export interface SubmitBenchmarkOfferParams {
  marketUuid: string;
  supplierDivisionUuid: string;
  expireOn?: string | null;
  schedule?: ScheduleInput | null;
  frequency?: 'one-time' | 'recurring';
  referrer?: Referrer;
}

const useSubmitBenchmarkOffer = () => {
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: ['submitBenchmarkOffer'],
    mutationFn: async (variables: SubmitBenchmarkOfferParams) => {
      const { marketUuid, supplierDivisionUuid, expireOn, schedule, frequency, referrer } = variables;
      const takerMarket = takerMarkets?.find(
        (tm) => tm.marketUuid === marketUuid && tm.takerDivisionUuid === supplierDivisionUuid
      );
      const uuid = takerMarket?.offerConfig.uuid;

      const response = uuid
        ? await apiClient.patch('api/supplier-experience/offers/benchmark', {
            json: {
              groupingKey: 'SUPPLIER_MARKET',
              status: determineOfferStatus(schedule),
              offers: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  uuid,
                },
              ],
              expirationDate: expireOn,
              schedule: buildSchedulePatchData(schedule, takerMarket),
              frequency,
              referrer,
            } satisfies paths['/offers/benchmark']['patch']['requestBody']['content']['application/json'],
          })
        : await apiClient.post('api/supplier-experience/offers/benchmark', {
            json: {
              groupingKey: 'SUPPLIER_MARKET',
              offerType: 'benchmark',
              frequency: mapFrequencyToApiFormat(frequency),
              supplierMarkets: [
                {
                  makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                  marketUuid,
                  supplierDivisionUuid,
                },
              ],
              expirationDate: expireOn ?? undefined, // Allow null to coalesce to undefined for POST because this is a net new offer.
              schedule: buildSchedulePostData(schedule),
              referrer,
            } satisfies paths['/offers/benchmark']['post']['requestBody']['content']['application/json'],
          });

      if (!uuid) {
        if (response.status === 201) {
          const body =
            await response.json<
              paths['/offers/benchmark']['post']['responses']['201']['content']['application/json']
            >();
          return { status: response.status, offerUuid: body.data[0]?.uuid };
        }

        if (response.status === 202) {
          const body =
            await response.json<
              paths['/offers/benchmark']['post']['responses']['202']['content']['application/json']
            >();
          return { status: response.status, offerUuid: body.data.offers[0]?.uuid };
        }
      }

      return { status: response.status };
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        // If placing first offer, use returned offer uuid from API response to update cache
        // User can interact with offer before stats and offer refetches
        const offerUuid = tmMeta?.offerConfig.uuid ?? result.offerUuid;

        const status = determineOfferStatus(variables.schedule);

        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => ({
            ...(tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid
              ? {
                  ...tm,
                  offerConfig: {
                    ...tm.offerConfig,
                    isEnabled: determineOptimisticIsEnabled(status, tmMeta.offerConfig.isEnabled ?? false),
                    uuid: offerUuid,
                    frequency: variables.frequency
                      ? mapFrequencyToCacheFormat(variables.frequency)
                      : tm.offerConfig.frequency,
                  },
                  disableAfterMarketClearsDate: variables.expireOn ?? null,
                  offerSchedule: buildScheduleOptimisticUpdateData(variables.schedule, tm),
                }
              : tm),
          }))
        );

        if (result.status === 202) {
          addPendingMarkets([
            {
              marketUuid: tmMeta.marketUuid,
              takerDivisionUuid: tmMeta.takerDivisionUuid,
              makerOrganizationUuid: tmMeta.makerOrganizationUuid,
            },
          ]);
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
        }
      }

      // Marketing artifacts can display messages promoting offers.
      // Invalidate so we stop marketing offers the supplier just submitted.
      invalidateContentArtifacts(queryClient);
    },
    onError: (error, variables) => {
      const takerMarket = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      addError('useSubmitBenchmarkOffer', {
        error,
        variables,
        seaOfferUuid: takerMarket?.offerConfig.uuid,
      });
    },
  });
};

export default useSubmitBenchmarkOffer;
